import { useTranslation } from 'react-i18next'

interface ErrorDisplayProps {
  error: string
  title?: string
  onBack?: {
    label: string
    action: () => void
  }
  onRefresh?: () => void
  fullScreen?: boolean
}

export default function ErrorDisplay({
  error,
  title,
  onBack,
  onRefresh,
  fullScreen = false,
}: ErrorDisplayProps) {
  const { t } = useTranslation('common')

  const content = (
    <div className="flex flex-col items-center justify-center gap-6 p-8 max-w-md mx-auto">
      {/* Error Icon */}
      <div className="relative">
        {/* Outer pulsing circle */}
        <div className="absolute inset-0 bg-red-500/20 rounded-full animate-ping"></div>

        {/* Icon container */}
        <div className="relative w-20 h-20 bg-linear-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg">
          <svg
            className="w-10 h-10 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
      </div>

      {/* Error Title */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {title || t('error.title', 'Error Occurred')}
        </h3>

        {/* Error Message */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-600 mt-0.5 shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-red-800 text-left flex-1">{error}</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3 w-full">
        {/* Back Button */}
        {onBack && (
          <button
            onClick={onBack.action}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors shadow-md"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span>{onBack.label}</span>
          </button>
        )}

        {/* Refresh Button */}
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors shadow-md"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>{t('error.refresh', 'Retry')}</span>
          </button>
        )}
      </div>

      {/* Help Text */}
      <p className="text-sm text-gray-500 text-center">
        {t('error.helpText', 'If the problem persists, please contact support.')}
      </p>
    </div>
  )

  // Full screen overlay (covers main content area only, not header/sidebar)
  // Note: Requires page component root to not have `position: relative`
  if (fullScreen) {
    return (
      <div className="absolute inset-0 bg-linear-to-br from-red-50/95 via-orange-50/95 to-red-50/95 backdrop-blur-md flex items-center justify-center z-[9999]">
        {content}
      </div>
    )
  }

  // Regular container (covers parent container)
  return (
    <div className="absolute inset-0 bg-linear-to-br from-red-50/95 via-orange-50/95 to-red-50/95 backdrop-blur-md flex items-center justify-center z-[9998]">
      {content}
    </div>
  )
}
